/**
 * The client half of the `free-watermarked-preview` experiment.
 *
 * Every video surface (the spec-driven /tools studios and the two hand-written
 * Seedance pages) asks the same question the same way, and each of their
 * routers answers it on its own `POST .../preview-eligibility`. The fetch is
 * here so the exposure rule — one call, only in front of a paywall a preview
 * could actually open — is stated once instead of per studio.
 */

export type PreviewEligibility = 'full' | 'preview' | 'blocked'

/**
 * Ask the server what this org would get, dealing its experiment arm.
 *
 * `null` means "could not tell" (network, no workspace yet): the caller keeps
 * whatever the balance already said rather than guessing at an arm.
 */
export async function fetchPreviewEligibility(
  endpoint: string,
  token: string | null,
  workspaceId: string | null,
): Promise<PreviewEligibility | null> {
  try {
    // web-arch-allow-direct-fetch: imperative marketing tool bootstrap.
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(workspaceId ? { workspaceId } : {}),
    })
    if (!res.ok) return null
    const body = (await res.json()) as {
      data?: { mode?: PreviewEligibility }
    }
    return body.data?.mode ?? null
  } catch {
    return null
  }
}
